import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { Descr } from "./VQF_902_1_customer.js";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_14 = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Special Clarifications",
    de: "Besondere Abklärungen",
  }),
  description: i18n.fixed({
    en: "When a business relationship or transaction is associated with increased risk, appears unusual or evidence exists that the assets are the proceeds of a felony or a qualified tax offence, the member has to perform additional clarifications.",
    de: "Wenn eine Geschäftsbeziehung oder Transaktion mit einem erhöhten Risiko verbunden ist, ungewöhnlich erscheint oder Anhaltspunkte dafür bestehen, dass die Vermögenswerte aus einem Verbrechen oder einem qualifizierten Steuervergehen stammen, hat das Mitglied zusätzliche Abklärungen durchzuführen.",
  }),
  id: "vqf_902_14",
  version: 1,
  config: VQF_902_14(i18n),
});

export function VQF_902_14(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Special Clarifications",
      de: "Besondere Abklärungen",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Information on customer",
          de: "Angaben zum Kunden",
        }),
        description: Descr.CUSTOMER_INFO_TYPE(i18n),
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_NAME,
            label: i18n.fixed({ en: "Customer", de: "Kunde" }),
            type: "text",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Reason for special clarifications",
          de: "Grund für besondere Abklärungen",
        }),
        fields: [
          {
            id: TalerFormAttributes.INCRISK_REASON,
            label: i18n.fixed({ en: "Reason", de: "Grund" }),
            help: i18n.fixed({
              en: "Description of the circumstances/transactions, which triggered the special clarifications",
              de: "Beschreibung der Umstände/Transaktionen, welche die besonderen Abklärungen ausgelöst haben",
            }),
            type: "textArea",
            required: true,
            validator(text, form) {
              return !text
                ? i18n.fixed({
                    en: "can't be empty",
                    de: "darf nicht leer sein",
                  })
                : undefined;
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Used means of clarification",
          de: "Verwendete Abklärungsmittel",
        }),
        fields: [
          {
            id: TalerFormAttributes.INCRISK_MEANS,
            label: i18n.fixed({ en: "Means", de: "Mittel" }),
            type: "choiceStacked",
            choices: [
              {
                value: "GATHERING",
                label: i18n.fixed({
                  en: "Gathering of information from the customer, beneficial owner of the assets, controlling person",
                  de: "Einholung von Informationen beim Kunden, wirtschaftlich Berechtigten der Vermögenswerte, dem Kontrollinhaber",
                }),
              },
              {
                value: "CONSULTATION",
                label: i18n.fixed({
                  en: "Consultation of generally accessible sources and databases",
                  de: "Konsultation allgemein zugänglicher Quellen und Datenbanken",
                }),
              },
              {
                value: "ENQUIRIES",
                label: i18n.fixed({
                  en: "Enquiries with trustworthy persons",
                  de: "Erkundigungen bei vertrauenswürdigen Personen",
                }),
              },
              {
                value: "OTHER",
                label: i18n.fixed({
                  en: "Other, which?",
                  de: "Andere, welche?",
                }),
              },
            ],
            required: true,
          },
          {
            id: TalerFormAttributes.INCRISK_MEANS_OTHER,
            type: "text",
            label: i18n.fixed({
              en: "Other means of clarification:",
              de: "Andere Abklärungsmittel:",
            }),
            required: true,
            hide(value, root) {
              return root[TalerFormAttributes.INCRISK_MEANS] !== "OTHER";
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Supplemental File Upload",
          de: "Hochladen zusätzlicher Dokumente",
        }),
        description: i18n.fixed({
          en: "Optional supplemental information for the special clarifications.",
          de: "Optionale ergänzende Angaben zu den besonderen Abklärungen.",
        }),
        fields: [
          {
            id: TalerFormAttributes.SUPPLEMENTAL_FILES_LIST,
            label: i18n.fixed({
              en: "Supplemental Files",
              de: "Zusätzliche Dokumente",
            }),
            type: "array",
            labelFieldId: "FILE.FILENAME",
            required: false,
            fields: [
              {
                id: TalerFormAttributes.DESCRIPTION,
                label: i18n.fixed({ en: "Description", de: "Beschreibung" }),
                type: "textArea",
                required: true,
              },
              {
                id: TalerFormAttributes.FILE,
                label: i18n.fixed({ en: "File (PDF)", de: "Datei (PDF)" }),
                type: "file",
                accept: "application/pdf",
                required: true,
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Summary and plausibility check of the gathered information",
          de: "Zusammenfassung und Plausibilitätsprüfung der eingeholten Informationen",
        }),
        description: i18n.fixed({
          en: "The results of the clarifications have to be documented and their plausibility has to be checked.",
          de: "Die Ergebnisse der Abklärungen sind zu dokumentieren und auf ihre Plausibilität zu prüfen.",
        }),
        fields: [
          {
            id: TalerFormAttributes.INCRISK_SUMMARY,
            label: i18n.fixed({ en: "Summary", de: "Zusammenfassung" }),
            type: "textArea",
            required: true,
          },
          {
            id: TalerFormAttributes.INCRISK_DOCUMENTS,
            label: i18n.fixed({
              en: "Gathered/Consulted documents",
              de: "Eingeholte/konsultierte Dokumente",
            }),
            type: "textArea",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Result of the special clarification",
          de: "Ergebnis der besonderen Abklärung",
        }),
        fields: [
          {
            id: TalerFormAttributes.INCRISK_RESULT,
            label: i18n.fixed({ en: "Result", de: "Ergebnis" }),
            type: "choiceStacked",
            choices: [
              {
                label: i18n.fixed({ en: "No suspicion", de: "Kein Verdacht" }),
                value: "NO_SUSPICION",
                description: i18n.fixed({
                  en: "The plausibility of the circumstances could be checked, no reasonable suspicion pursuant to Art. 9 AMLA (possibly update of customer profile and/or risk profile)",
                  de: "Die Plausibilität der Umstände konnte überprüft werden, kein begründeter Verdacht gemäss Art. 9 GwG (gegebenenfalls Aktualisierung des Kundenprofils und/oder Risikoprofils)",
                }),
              },
              {
                label: i18n.fixed({
                  en: "Reasonable suspicion",
                  de: "Begründeter Verdacht",
                }),
                value: "REASONABLE_SUSPICION",
                description: i18n.fixed({
                  en: "Reasonable suspicion pursuant to Art. 9 AMLA, duty to file a report with MROS",
                  de: "Begründeter Verdacht gemäss Art. 9 GwG, Meldepflicht bei der MROS",
                }),
              },
              {
                label: i18n.fixed({
                  en: "Simple suspicion",
                  de: "Einfacher Verdacht",
                }),
                value: "SIMPLE_SUSPICION",
                description: i18n.fixed({
                  en: "Simple suspicion pursuant to Art. 305ter Para. 2 StGB, right to notify MROS",
                  de: "Einfacher Verdacht gemäss Art. 305ter Abs. 2 StGB, Melderecht gegenüber der MROS",
                }),
              },
              {
                label: i18n.fixed({ en: "Other, what?", de: "Andere, was?" }),
                value: "OTHER",
              },
            ],
            required: true,
          },
          {
            id: TalerFormAttributes.INCRISK_RESULT_OTHER,
            type: "text",
            label: i18n.fixed({
              en: "Result clarification",
              de: "Erläuterung des Ergebnisses",
            }),
            required: true,
            hide(value, root) {
              return root[TalerFormAttributes.INCRISK_RESULT] !== "OTHER";
            },
          },
        ],
      },
    ],
  };
}
